import { Alert, AlertDescription, AlertVariant } from '@/components/ui/alert'
import { Link } from '@/components/ui/link'
import { Query } from '@/lib/generated/graphql-types'
import { useG2GTracking } from '@/modules/salary/analytics/use-g2g-tracking'
import { useIntersectionObserver } from '@/modules/salary/analytics/use-intersection-observer'
import { useSignupTracking } from '@/modules/salary/analytics/use-signup-tracking'
import { SalaryFilterControl } from '../constants'
import { useSalaryFilters } from '@/modules/salary/filters/use-salary-filters'
import {
  SalaryRestrictionType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import { OnboardingAccountType } from '@/modules/surveys/configs/onboarding/constants'
import { GET_USER_PROFILE } from '@/modules/user/profile/queries'
import { MainRoutes } from '@/utils/routes'
import { useQuery } from '@apollo/client'
import { motion } from 'framer-motion'
import { WorkStatus } from './constants'

function SalaryAlertContainer({
  children,
  variant,
  dataTestId,
}: {
  children: React.ReactNode
  variant: AlertVariant
  dataTestId: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: '-1rem' }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: '-1rem' }}
    >
      <Alert
        className="max-w-screen-2xl w-[calc(100%-2rem)] 2xl:w-full mx-auto my-4"
        variant={variant}
      >
        <AlertDescription data-testid={dataTestId}>{children}</AlertDescription>
      </Alert>
    </motion.div>
  )
}
/**
 * Follows business logic from the backend defined in
 * https://www.notion.so/marithealth/User-Roles-15f1ff33e27480a6acdecef50b7c2d47
 */
export function SalaryAlerts({
  hasReachedPublicLimit,
  hasSalaries,
  isAuthenticated,
}: {
  hasReachedPublicLimit?: boolean
  hasSalaries?: boolean
  isAuthenticated: boolean
}): React.JSX.Element | null {
  const { salaryFilterControls } = useSalaryFilters({
    isAuthenticated,
  })
  const { useG2GViewTracking } = useG2GTracking()
  const { trackSignupClick } = useSignupTracking()
  const handleVisible = useG2GViewTracking({
    moduleName: 'main',
  })
  const elementRef = useIntersectionObserver<HTMLAnchorElement>({
    onIntersect: handleVisible,
    triggerOnce: true,
  })
  const {
    data: userProfileData,
    loading: isLoadingUserProfile,
    called: isCalledUserProfile,
  } = useQuery<Pick<Query, 'getUserProfile'>>(GET_USER_PROFILE, {
    skip: !isAuthenticated,
  })

  const salaryRestriction = useSalaryTableStore(
    (state) => state.salaryRestriction,
  )

  const professionFilter =
    salaryFilterControls?.[SalaryFilterControl.profession]
  const isStudent =
    userProfileData?.getUserProfile?.onboardingType ===
    OnboardingAccountType.Student
  const isEmployer =
    userProfileData?.getUserProfile?.onboardingType ===
    OnboardingAccountType.Employer
  const isPracticing =
    userProfileData?.getUserProfile?.workStatus === WorkStatus.Practicing
  const isOnboardingComplete = Boolean(
    userProfileData?.getUserProfile?.onboardedAt,
  )
  const didCompleteInvite = Boolean(
    userProfileData?.getUserProfile?.inviteCompletedAt,
  )
  const userProfesssionSlug =
    userProfileData?.getUserProfile?.occupation?.profession?.slug

  const isSearchingDifferentProfession =
    professionFilter &&
    !isStudent &&
    !isEmployer &&
    professionFilter !== userProfesssionSlug
  const accessExpiresAt = userProfileData?.getUserProfile?.accessExpiresAt
  const isExpired = accessExpiresAt
    ? new Date(accessExpiresAt) <= new Date()
    : false

  const isUnauthedWithLimit = !isAuthenticated && hasReachedPublicLimit

  if (!isCalledUserProfile || isLoadingUserProfile) return null

  if (isUnauthedWithLimit) {
    return (
      <SalaryAlertContainer
        variant="error"
        dataTestId="salary-alerts-unauthed-limit"
      >
        You do not yet have full access to all salary details. Our community
        uses a &apos;give-to-get&apos; model - please{' '}
        <Link
          href={MainRoutes.Onboarding}
          ref={elementRef}
          onClick={() => trackSignupClick('main')}
          className="inline whitespace-break-spaces"
        >
          add your anonymous salary
        </Link>{' '}
        to unlock access to salary details.
      </SalaryAlertContainer>
    )
  }
  if (!isAuthenticated) return null

  if (!isOnboardingComplete) {
    return (
      <SalaryAlertContainer
        variant="error"
        dataTestId="salary-alerts-onboarding-incomplete"
      >
        You do not yet have full access to all salary details. Our community
        uses a &apos;give-to-get&apos; model - please{' '}
        <Link
          href={MainRoutes.Onboarding}
          ref={elementRef}
          onClick={() => trackSignupClick('main')}
          className="whitespace-break-spaces inline"
        >
          complete your account setup
        </Link>{' '}
        to unlock access to salaries.
      </SalaryAlertContainer>
    )
  }

  if (!hasSalaries) return null

  /**
   * Student restriction is added when they haven't invited anyone yet
   * and expired is after invitation when they lost access and need to re-invite
   * but is same message for both cases
   */
  if (
    salaryRestriction === SalaryRestrictionType.Student ||
    (isStudent && isExpired)
  ) {
    return (
      <SalaryAlertContainer
        variant="warning"
        dataTestId="salary-alerts-expired-access-student"
      >
        Student accounts have limited access to salary details -{' '}
        <Link
          href={MainRoutes.Invite}
          className="inline whitespace-break-spaces"
        >
          refer a friend
        </Link>{' '}
        to get unlimited access for 1 full year.
      </SalaryAlertContainer>
    )
  }

  if (isExpired) {
    if (isPracticing) {
      return (
        <SalaryAlertContainer
          variant="warning"
          dataTestId="salary-alerts-expired-access-practicing-physician-or-app"
        >
          It&apos;s been more than 1 year since your last salary. To renew
          access - please{' '}
          <Link
            href={MainRoutes.Onboarding}
            className="inline whitespace-break-spaces"
          >
            add an updated salary
          </Link>{' '}
          so we can keep the data fresh for the community.
        </SalaryAlertContainer>
      )
    }
    if (!didCompleteInvite) {
      return (
        <SalaryAlertContainer
          variant="warning"
          dataTestId="salary-alerts-expired-access-nonpracticing-no-invite"
        >
          Your access has expired and some salary information below has been
          hidden -{' '}
          <Link
            href={MainRoutes.Invite}
            className="inline whitespace-break-spaces"
          >
            refer a friend or colleague
          </Link>{' '}
          to Marit to renew your access to salary details.
        </SalaryAlertContainer>
      )
    }
    return (
      <SalaryAlertContainer
        variant="warning"
        dataTestId="salary-alerts-expired-access-with-invite"
      >
        It&apos;s been more than 1 year since we last activated your account. To
        renew access - please{' '}
        <Link
          href={MainRoutes.Profile}
          className="inline whitespace-break-spaces"
        >
          update your account details
        </Link>{' '}
        so we can keep the data fresh for the community.
      </SalaryAlertContainer>
    )
  }

  if (salaryRestriction === SalaryRestrictionType.Employer) {
    return (
      <SalaryAlertContainer
        variant="default"
        dataTestId="salary-alerts-employer-access"
      >
        Some salary information below has been hidden - employers accounts have
        limited access to salary details.
      </SalaryAlertContainer>
    )
  }

  if (isSearchingDifferentProfession) {
    return (
      <SalaryAlertContainer
        variant="default"
        dataTestId="salary-alerts-searching-different-profession"
      >
        Some salary information below has been hidden - salary details are only
        available for your profession.
      </SalaryAlertContainer>
    )
  }

  return <div data-testid="salary-alert-none" />
}
